import React from "react";
import { DefaultButton, PrimaryButton, Stack, Text, TextField } from "@fluentui/react";

const shouldPreferTranscript = (typeSlug) => {
    if (!typeSlug) {
        return false;
    }

    const normalizedTypeSlug = typeSlug.trim().toLowerCase();
    return (
        normalizedTypeSlug === "audio" ||
        normalizedTypeSlug === "infographics" ||
        normalizedTypeSlug === "videos" ||
        normalizedTypeSlug === "full-webinar-videos" ||
        normalizedTypeSlug === "webinar-clip-videos"
    );
};

const getSourceTextDisplay = (content) => {
    if (shouldPreferTranscript(content?.typeSlug) && content?.transcript) {
        return content.transcript;
    }

    return content?.contentText || "";
};

const ContentSummaryEditor = ({
    activeContent,
    summaryDraft,
    setSummaryDraft,
    onSaveSummary,
    isBusy,
}) => {
    if (!activeContent) {
        return <Text>Select a content row to review or edit its summary.</Text>;
    }

    return (
        <>
            <Text>
                Editing content {activeContent.contentId}: {activeContent.title}
            </Text>
            <Text styles={{ root: { color: "#605e5c" } }}>
                Showing: {activeContent.summarySourceLabel || "No summary yet"}
            </Text>
            {activeContent.isPlaceholderSummary && !!activeContent.effectiveSummary && (
                <Text styles={{ root: { color: "#605e5c" } }}>
                    This is a placeholder summary generated from the current content because no saved Summary column in the content table exists yet.
                </Text>
            )}
            <Text styles={{ root: { color: "#605e5c", maxWidth: 1100 } }}>
                "Content text" is the source content used for review. The following textboxes will be empty if this is empty. It is <strong>read-only</strong> here so you can compare the text against the preview and saved summary before making changes.
            </Text>
            <TextField
                label="Content text"
                multiline
                readOnly
                value={getSourceTextDisplay(activeContent)}
                rows={20}
                resizable={false}
                styles={{
                    root: { maxWidth: 1100 },
                    fieldGroup: { height: 480, minHeight: 480 },
                    field: { height: 480, minHeight: 480, overflowY: "auto" }
                }}
            />
            <Text styles={{ root: { color: "#605e5c", maxWidth: 1100 } }}>
                "Displayed summary preview" shows what users currently see in the Content API Summary property. It is <strong>read-only</strong>. If no saved summary exists, this area shows the fallback placeholder <strong>(non-ai)</strong> summary generated from the content.
            </Text>
            <TextField
                label="Displayed summary preview"
                multiline
                readOnly
                value={activeContent.effectiveSummary || ""}
                rows={4}
                resizable={false}
                styles={{
                    root: { maxWidth: 1100 },
                    field: { overflowY: "auto" }
                }}
            />
            <Text styles={{ root: { color: "#605e5c", maxWidth: 1100 } }}>
                Update the "Saved summary" field to a piece of content. Once saved, this value replaces the placeholder summary for this content.
            </Text>
            <TextField
                label="Saved summary"
                multiline
                autoAdjustHeight
                value={summaryDraft}
                onChange={(_, value) => setSummaryDraft(value || "")}
            />
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                <PrimaryButton
                    text="Save Summary"
                    onClick={onSaveSummary}
                    disabled={isBusy}
                />
                <DefaultButton
                    text="Reset"
                    onClick={() => setSummaryDraft(activeContent.summary || "")}
                    disabled={isBusy}
                />
            </Stack>
        </>
    );
};

export default ContentSummaryEditor;
